import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { DeviatedValueCellRendererComponent } from 'components/AgGrid/DeviatedValueCellRendererComponent';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { ConstructionTypeData, ContractResultData, ContractResultsViewData } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { getNumberFormatterWithLinePlaceholder, numberFormatterWithLinePlaceholder } from 'pages/Project/Common/utils';
import { formatDateToLocale } from 'utils';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';

import { CollapseKeysHeaderComponent } from '../components';
import { ConstructionTypeContractPlanCellRenderer } from '../components/ConstructionTypeContractPlanCellRenderer';
import { NameCellRenderer } from '../components/NameCellRenderer';

interface IUseAccumulationSheetColumns {
    contractTitle: string;
    isContractPlanOnly?: boolean;
    data?: ContractResultsViewData;
    constructionTypes: ConstructionTypeData[] | undefined | null;
    hasNoPlanByConstructionType?: boolean | null;
    costRounding: number;
}

const suppressKeyboardEvent = () => true;

const instanceId = 'uerAccumulationSheetTable';

const getConstructionTypeFieldKey = (id: string | number) => `constructionType.${id}.contractPlan`;

export const useAccumulationSheetColumns = (props: IUseAccumulationSheetColumns) => {
    const {
        contractTitle,
        isContractPlanOnly,
        data: { workOrders = [], contractResults } = {},
        constructionTypes: constructionTypesDefault = [],
        hasNoPlanByConstructionType,
        costRounding,
    } = props;

    const constructionTypes = useMemo(
        () => (hasNoPlanByConstructionType ? [] : constructionTypesDefault),
        [constructionTypesDefault, hasNoPlanByConstructionType],
    );

    const workOrderConstructionTypesIds = useMemo(
        () =>
            contractResults?.[0]?.workOrderResults?.reduce(
                (acc, result) => {
                    acc[result.workOrderId] = result.constructionTypeResults.map((r) => r.constructionTypeId) || [];

                    return acc;
                },
                {} as Record<string, number[]>,
            ),
        [contractResults],
    );

    const [selection, , , setSelection] = useSelection(instanceId);
    const [pinnedItems, , , setPinned] = usePin(instanceId);

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const allColumns = useMemo(() => {
        const mainColumns = [
            {
                headerName: contractTitle,
                field: 'name',
                width: 540,
                suppressKeyboardEvent,
                lockPosition: 'left',
                spanHeaderHeight: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                cellRenderer: NameCellRenderer,
            },
            {
                headerName: 'План по договору',
                suppressKeyboardEvent,
                spanHeaderHeight: true,
                headerClass: 'center-aligned-header',
                headerGroupComponent: hasNoPlanByConstructionType ? undefined : CollapseKeysHeaderComponent,
                headerGroupComponentParams: hasNoPlanByConstructionType
                    ? undefined
                    : {
                          label: 'План по договору',
                          collapseKeys: constructionTypes?.map((v) => getConstructionTypeFieldKey(v.id)),
                      },
                marryChildren: true,
                children: [
                    {
                        headerName: 'Итого',
                        field: 'contractPlan',
                        valueFormatter: getNumberFormatterWithLinePlaceholder(costRounding),
                        spanHeaderHeight: true,
                        cellClass: 'ag-right-aligned-cell',
                        ...(isContractPlanOnly ? { flex: 1 } : { width: 200 }),
                    },
                    ...(constructionTypes ?? []).map((v) => ({
                        headerName: v.name,
                        field: getConstructionTypeFieldKey(v.id),
                        spanHeaderHeight: true,
                        cellClass: 'ag-right-aligned-cell',
                        ...(isContractPlanOnly ? { flex: 1 } : { width: 200 }),
                        headerClass: 'breakWord-header',
                        cellRenderer: ConstructionTypeContractPlanCellRenderer,
                        cellRendererParams: {
                            costRounding,
                            constructionType: v,
                        },
                    })),
                ],
            },
        ];

        const additionalColumns = [
            {
                headerName: 'Сумма планов НЗ',
                field: 'workOrderPlanSum',
                valueFormatter: getNumberFormatterWithLinePlaceholder(costRounding),
                spanHeaderHeight: true,
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviation: true,
                    deviationKey: 'deviation',
                },
                suppressKeyboardEvent,
                headerClass: 'center-aligned-header',
                width: 200,
            },
            {
                headerName: 'Остаток с учетом планов НЗ',
                field: 'contractRest',
                valueFormatter: getNumberFormatterWithLinePlaceholder(costRounding),
                spanHeaderHeight: true,
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviationTip: true,
                    deviationTipKey: 'deviationTip',
                },
                suppressKeyboardEvent,
                headerClass: 'center-aligned-header breakWord-header',
                width: 200,
            },
            {
                headerName: 'Факт освоения по НЗ',
                field: 'completed',
                valueFormatter: getNumberFormatterWithLinePlaceholder(costRounding),
                spanHeaderHeight: true,
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviation: true,
                    deviationKey: 'completedDeviation',
                },
                suppressKeyboardEvent,
                headerClass: 'center-aligned-header breakWord-header',
                width: 200,
            },
            {
                headerName: 'Остаток с учетом освоения',
                field: 'completedRest',
                valueFormatter: getNumberFormatterWithLinePlaceholder(costRounding),
                spanHeaderHeight: true,
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviationTip: true,
                    deviationTipKey: 'completedRestDeviationTip',
                },
                headerClass: 'center-aligned-header breakWord-header',
                width: 200,
            },
            ...(workOrders ?? []).map((workOrder) => ({
                spanHeaderHeight: true,
                headerGroupComponent: hasNoPlanByConstructionType ? undefined : CollapseKeysHeaderComponent,
                headerName: `НЗ ${workOrder.internalNumber ? `№ ${workOrder.internalNumber}` : ''} ${
                    workOrder.signingDate ? `от ${formatDateToLocale(workOrder.signingDate)}` : ''
                }`,
                headerGroupComponentParams: hasNoPlanByConstructionType
                    ? undefined
                    : {
                          label: `НЗ ${workOrder.internalNumber ? `№ ${workOrder.internalNumber}` : ''} ${
                              workOrder.signingDate ? `от ${formatDateToLocale(workOrder.signingDate)}` : ''
                          }`,
                          isUseNavigateBtn: true,
                          workOrderId: workOrder.id,
                          collapseKeys: constructionTypes
                              ?.filter((t) => workOrderConstructionTypesIds?.[workOrder.id]?.includes(t.id))
                              .reduce((acc: string[], v) => {
                                  acc.push(`${workOrder.id}_${v.id}_plan`);
                                  acc.push(`${workOrder.id}_${v.id}_actSum`);

                                  return acc;
                              }, []),
                      },
                marryChildren: true,
                children: [
                    {
                        headerName: 'Итого',
                        marryChildren: true,
                        children: [
                            {
                                headerName: 'План',
                                valueFormatter: ({ data }: { data: ContractResultData }) => {
                                    const value = data?.workOrderResults?.find((c) => c.workOrderId === workOrder.id)
                                        ?.planPrice;

                                    return numberFormatterWithLinePlaceholder(
                                        { value, dataType: data?.type },
                                        costRounding,
                                    );
                                },
                                cellClass: 'ag-right-aligned-cell',
                            },
                            {
                                headerName: 'Сумма Актов КС-2',
                                valueFormatter: ({ data }: { data: ContractResultData }) => {
                                    const value = data?.workOrderResults?.find((c) => c.workOrderId === workOrder.id)
                                        ?.completedPrice;

                                    return numberFormatterWithLinePlaceholder(
                                        { value, dataType: data?.type },
                                        costRounding,
                                    );
                                },
                                cellClass: 'ag-right-aligned-cell',
                            },
                        ],
                    },
                    ...(constructionTypes ?? [])
                        .filter((t) => workOrderConstructionTypesIds?.[workOrder.id]?.includes(t.id))
                        .map((v) => ({
                            headerName: v.name,
                            spanHeaderHeight: true,
                            headerClass: 'breakWord-header',
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'План',
                                    field: `${workOrder.id}_${v.id}_plan`,
                                    valueFormatter: ({ data }: { data: ContractResultData }) => {
                                        const value = data?.workOrderResults
                                            ?.find((c) => c.workOrderId === workOrder.id)
                                            ?.constructionTypeResults.find((ct) => ct.constructionTypeId === v.id)
                                            ?.planPrice;

                                        return numberFormatterWithLinePlaceholder(
                                            { value, dataType: data?.type },
                                            costRounding,
                                        );
                                    },
                                    cellClass: 'ag-right-aligned-cell',
                                },
                                {
                                    headerName: 'Сумма Актов Кс-2',
                                    field: `${workOrder.id}_${v.id}_actSum`,
                                    valueFormatter: ({ data }: { data: ContractResultData }) => {
                                        const value = data?.workOrderResults
                                            ?.find((c) => c.workOrderId === workOrder.id)
                                            ?.constructionTypeResults.find((ct) => ct.constructionTypeId === v.id)
                                            ?.completedPrice;

                                        return numberFormatterWithLinePlaceholder(
                                            { value, dataType: data?.type },
                                            costRounding,
                                        );
                                    },
                                    cellClass: 'ag-right-aligned-cell',
                                },
                            ],
                        })),
                ],
            })),
        ];

        return isContractPlanOnly ? mainColumns : [...mainColumns, ...additionalColumns];
    }, [
        constructionTypes,
        contractTitle,
        costRounding,
        hasNoPlanByConstructionType,
        isContractPlanOnly,
        workOrderConstructionTypesIds,
        workOrders,
    ]);

    const columns = useMemo(() => {
        const columnsWithPins = getColumnsWithPinnedProp({ columns: allColumns as any, pinnedItems });

        return columnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [allColumns, isColumnVisible, pinnedItems]);

    const optionalColumns = useMemo(
        () =>
            allColumns.reduce((acc: ColDef[], column, idx) => {
                const newColumn: any = { ...column };

                if (!idx) {
                    newColumn.disabled = true;
                }

                if (pinnedItems[getColumnId(column as any)]) {
                    newColumn.pinned = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [allColumns, pinnedItems],
    );

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: optionalColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [optionalColumns, setSelection, setPinned],
    );

    useEffect(() => {
        setPinned({ [contractTitle]: true });
    }, [contractTitle, setPinned]);

    return {
        columns,
        isColumnVisible,
        handleTableSettingsConfirm,
        optionalColumns,
    };
};
